import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Tabs from "../Common/Tabs";
import AddIleggelseModalContainer from "./AddIleggelseModalContainer";
import IleggeseDetailTabsContainer from "./IleggelseDetailTabsContainer";
import { authorized } from "../../utils/authorizationUtils";
import { juridiskAll } from "../../constants/roles";
import {
  getIleggelseDetailByNummer,
  deleteKlageIleggelse,
  getKlageIleggelser,
  getAllBilderInBase64,
  getAllMiljoKontrollBilderInBase64,
} from "../../actions/klager/klageAction";
import { getIleggelseRettelser } from "../../actions/ileggelseRettelserActions";
import { getMiljoRettelser } from "../../actions/miljoRettelserActions";
import { filter, map, first, size } from "lodash";

function IleggelseNumberTabsContainer({ manglerInfo }) {
  const dispatch = useDispatch();
  const klageId = useSelector((state) =>
    state.klageDetail && state.klageDetail.id ? state.klageDetail.id : "",
  );
  const klageIleggelse = useSelector((state) => state.klageIleggelse);
  const klageIleggelser = useSelector((state) => state.klageIleggelser);

  const [ileggelsetype, setIleggelsetype] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [isThumbAndLightboxImg, setIsThumbAndLightboxImg] = useState(true);

  const filterIleggelsesnummers = (klageIleggelserParam = undefined) => {
    if (klageIleggelserParam) {
      return map(klageIleggelserParam, "ileggelsesnummer");
    }
    return map(klageIleggelser, "ileggelsesnummer");
  };

  const getTypeOfIleggelse = (
    ileggelsesnummer,
    klageIleggelserParam = undefined,
  ) => {
    const source = klageIleggelserParam || klageIleggelser;
    const ileggelse = first(
      filter(source, (i) => i.ileggelsesnummer === ileggelsesnummer),
    );
    const type = ileggelse.ileggelsetype.kode;
    setIleggelsetype(type);
    return type;
  };

  const getIleggelseDetails = (
    ileggelsesnummer,
    klageIleggelserParam = undefined,
  ) => {
    if (ileggelsesnummer) {
      const type = getTypeOfIleggelse(ileggelsesnummer, klageIleggelserParam);
      dispatch(getIleggelseDetailByNummer(ileggelsesnummer, type));
    }
  };

  const getIleggelseTilbakeMeldinger = async (
    ileggelsesnummer,
    klageIleggelserParam = undefined,
  ) => {
    const klageIleggelserVal = klageIleggelserParam || klageIleggelser;
    const ileggelse = first(
      filter(
        klageIleggelserVal,
        (i) => i.ileggelsesnummer === ileggelsesnummer,
      ),
    );
    if (!ileggelse) return null;
    const ileggelseId = ileggelse.ileggelseId;
    const type = ileggelse.ileggelsetype.kode;
    setIleggelsetype(type);
    type && type !== "M"
      ? dispatch(getIleggelseRettelser(ileggelseId))
      : dispatch(getMiljoRettelser(ileggelseId));
  };

  const getImages = async (
    ileggelsesnummer,
    klageIleggelserParam = undefined,
  ) => {
    const klageIleggelserVal = klageIleggelserParam || klageIleggelser;
    const ileggelse = first(
      filter(
        klageIleggelserVal,
        (i) => i.ileggelsesnummer === ileggelsesnummer,
      ),
    );
    if (!ileggelse) return null;
    const ileggelseId = ileggelse.ileggelseId;
    const type = ileggelse.ileggelsetype.kode;
    setIsThumbAndLightboxImg(true);
    dispatch(getAllBilderInBase64(ileggelseId, type, "thumbnail")).catch(() => {
      setIsThumbAndLightboxImg(false);
      dispatch(getAllBilderInBase64(ileggelseId, type));
    });
    dispatch(getAllBilderInBase64(ileggelseId, type, "lightbox")).catch(
      () => {},
    );
  };

  const fetchFirstIleggelseDetailTab = async (fetchedIleggelser) => {
    const ileggelseNumbers = filterIleggelsesnummers(fetchedIleggelser);
    if (ileggelseNumbers && size(ileggelseNumbers) > 0) {
      const firstIleggelsenummer = first(ileggelseNumbers);
      getIleggelseDetails(firstIleggelsenummer, fetchedIleggelser);
      setSelectedTab(0);
      getIleggelseTilbakeMeldinger(firstIleggelsenummer, fetchedIleggelser);
      getImages(firstIleggelsenummer, fetchedIleggelser);
    }

    if (klageIleggelse && klageIleggelse.miljoKontrollId) {
      dispatch(
        getAllMiljoKontrollBilderInBase64(
          klageIleggelse.miljoKontrollId,
          "thumbnail",
        ),
      ).catch(() => {
        dispatch(
          getAllMiljoKontrollBilderInBase64(klageIleggelse.miljoKontrollId),
        );
      });
      dispatch(
        getAllMiljoKontrollBilderInBase64(
          klageIleggelse.miljoKontrollId,
          "lightbox",
        ),
      );
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(getKlageIleggelser(klageId)).then((fetchedIleggelser) => {
      fetchFirstIleggelseDetailTab(fetchedIleggelser);
    });
  }, []);

  const prevKlageIleggelseIdRef = useRef(undefined);
  useEffect(() => {
    if (
      klageIleggelse.id &&
      prevKlageIleggelseIdRef.current !== klageIleggelse.id &&
      klageIleggelse.miljoKontrollId
    ) {
      dispatch(
        getAllMiljoKontrollBilderInBase64(
          klageIleggelse.miljoKontrollId,
          "thumbnail",
        ),
      ).catch(() => {
        dispatch(
          getAllMiljoKontrollBilderInBase64(klageIleggelse.miljoKontrollId),
        );
      });
      dispatch(
        getAllMiljoKontrollBilderInBase64(
          klageIleggelse.miljoKontrollId,
          "lightbox",
        ),
      );
    }
    prevKlageIleggelseIdRef.current = klageIleggelse.id;
  }, [klageIleggelse.id, klageIleggelse.miljoKontrollId]);

  const onAddIleggelse = async (ileggelsenummer) => {
    setSelectedTab(0);
    await getIleggelseTilbakeMeldinger(ileggelsenummer);
    await getImages(ileggelsenummer);
  };

  const onClickTitle = (e, index, ileggelsesnummer) => {
    setSelectedTab(index);
    getIleggelseDetails(ileggelsesnummer);
    getIleggelseTilbakeMeldinger(ileggelsesnummer);
    getImages(ileggelsesnummer);
  };

  const onRemveKlageIleggelse = async (e, ileggelsesnummer) => {
    const ileggelse = first(
      filter(klageIleggelser, (i) => i.ileggelsesnummer === ileggelsesnummer),
    );
    await dispatch(deleteKlageIleggelse(klageId, ileggelse.ileggelseId));
    await dispatch(getKlageIleggelser(klageId)).then((fetchedIleggelser) => {
      fetchFirstIleggelseDetailTab(fetchedIleggelser);
    });
  };

  const ileggelseNummerList = filterIleggelsesnummers();
  const onlyForJurisk = authorized(juridiskAll);

  return (
    <div className="klageIleggelserTabs">
      {onlyForJurisk && !manglerInfo && (
        <AddIleggelseModalContainer
          onAddIleggelse={onAddIleggelse}
          manglerInfo={manglerInfo}
        />
      )}
      <Tabs
        titles={ileggelseNummerList}
        titleHasCloseIcon
        onCloseIconClick={onRemveKlageIleggelse}
        onClickTitle={onClickTitle}
        selectedTab={selectedTab}
        authorizedToRemove={onlyForJurisk}
      >
        {ileggelseNummerList.length > 0 &&
          map(ileggelseNummerList, (i) => (
            <IleggeseDetailTabsContainer
              klageIleggelse={klageIleggelse}
              key={i}
              ileggelsesType={ileggelsetype}
              isThumbAndLightboxImg={isThumbAndLightboxImg}
            />
          ))}
      </Tabs>
    </div>
  );
}

IleggelseNumberTabsContainer.propTypes = {
  manglerInfo: PropTypes.bool,
};

export default IleggelseNumberTabsContainer;
